import React, { useState } from 'react';
import AuthService from '../../services/authService';
import '../admin/AdminDashboard.css';

const AgentLogin = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Password reset state
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      // Use the centralized auth service for login
      const data = await AuthService.login(email, password);
      
      // Verify the user is an agent
      if (data.user && data.user.role !== 'agent') {
        throw new Error('You do not have agent permissions. Please contact an administrator.');
      }
      
      // Call the success callback with the user data
      onLoginSuccess(data.user);
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'An error occurred during login');
    } finally {
      setIsLoading(false);
    }
  };

  const openResetModal = () => {
    setShowResetModal(true);
    setResetEmail(email); // Pre-fill with login email if available
    setResetError('');
    setResetSuccess('');
  };

  const closeResetModal = () => {
    setShowResetModal(false);
    setResetEmail('');
    setResetError('');
    setResetSuccess('');
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setIsResetting(true);
    setResetError('');
    setResetSuccess('');

    if (!resetEmail) {
      setResetError('Please enter your email address');
      setIsResetting(false);
      return;
    }

    try {
      const response = await fetch('https://back-end-x7tx.onrender.com/api/auth/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resetEmail })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send reset email');
      }

      setResetSuccess('Password reset email sent! Check your inbox for instructions.');
      setTimeout(() => {
        closeResetModal();
      }, 3000);

    } catch (error) {
      setResetError(error.message || 'An error occurred while sending reset email');
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="login-container content-container">
      <h2 className="section-title">Agent Login</h2>
      
      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label">Email Address</label>
          <input 
            type="email" 
            className="form-control" 
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="your.email@example.com"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Password</label>
          <input 
            type="password" 
            className="form-control" 
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            placeholder="Your password"
          />
        </div>
        
        <div className="form-group">
          <button 
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
        </div>
        
        <div className="mt-4 text-center">
          <button 
            type="button"
            onClick={openResetModal}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255, 255, 255, 0.5)',
              cursor: 'pointer',
              fontSize: '13px',
              padding: 0,
              textDecoration: 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)';
            }}
          >
            Reset Password
          </button>
          <p className="text-text-medium mt-2">
            Need an account? Please contact your administrator.
          </p>
        </div>
      </form>

      {/* Password Reset Modal */}
      {showResetModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px',
              borderBottom: '1px solid #dee2e6'
            }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#1a1a1a' }}>Reset Password</h3>
              <button
                type="button"
                onClick={closeResetModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6c757d'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ padding: '20px' }}>
              {resetError && (
                <div className="alert alert-danger mb-3">
                  {resetError}
                </div>
              )}

              {resetSuccess && (
                <div className="alert alert-success mb-3">
                  {resetSuccess}
                </div>
              )}

              <form onSubmit={handlePasswordReset}>
                <div className="form-group">
                  <label style={{ color: '#1a1a1a', display: 'block', marginBottom: '8px' }}>
                    Enter your email address and we'll send you a link to reset your password.
                  </label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="form-control"
                    placeholder="your.email@example.com"
                    required
                    style={{ color: '#1a1a1a' }}
                  />
                </div>

                <div style={{
                  display: 'flex',
                  gap: '10px',
                  justifyContent: 'flex-end',
                  marginTop: '20px'
                }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeResetModal}
                    disabled={isResetting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isResetting}
                  >
                    {isResetting ? 'Sending...' : 'Send Reset Email'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AgentLogin;